import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  ClientOnly,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { isChunkLoadError, reloadOnceForChunkError } from "../lib/chunk-reload";
import { supabase } from "@/integrations/supabase/client";
import { AuthProvider } from "@/hooks/use-auth";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { Toaster } from "@/components/ui/sonner";
import { CookieConsent } from "@/components/cookie-consent";
import { InstallAppBanner } from "@/components/install-app-banner";
import { BuyerAssistantWidget } from "@/components/buyer-assistant-widget";
import { registerServiceWorker } from "@/lib/pwa";
import { PushEnableBanner } from "@/components/push-enable-banner";
import { CurrencyProvider } from "@/lib/currency/provider";
import {
  DEFAULT_LOCALE,
  I18nProvider,
  LOCALES,
  LOCALE_PREFIX,
  OG_LOCALE,
  SITE_LOCALE_HREFLANG,
  detectLocale,
  stripLocalePrefix,
  type Locale,
} from "@/i18n";



function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="font-display text-7xl text-gold-gradient">404</h1>
        <h2 className="mt-4 text-xl font-semibold">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          This auction lot doesn't exist or has been withdrawn.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-gold-gradient px-4 py-2 text-sm font-medium text-gold-foreground transition-opacity hover:opacity-90"
          >
            Back to auctions
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: unknown; reset: () => void } & Record<string, unknown>) {
  console.error(error);
  const router = useRouter();
  const staleCode = isChunkLoadError(error);
  const detail =
    error instanceof Error
      ? `${error.name}: ${error.message}\n${(error.stack ?? "").split("\n").slice(0, 6).join("\n")}`
      : String(error);
  useEffect(() => {
    // Preview-only diagnostics: surface the real message so phone crashes can be traced.
    if (import.meta.env.DEV) console.warn("[root error detail]", detail);
    reportLovableError(error, { boundary: "tanstack_root_error_component", stale_code: staleCode });
    // Old code files can't be re-fetched in place; load the page fresh once.
    if (staleCode) reloadOnceForChunkError();
  }, [error, staleCode, detail]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold">Something went wrong</h1>
        <p className="mt-2 text-sm text-muted-foreground">Please try again or head home.</p>
        {import.meta.env.DEV && (
          <pre className="mt-4 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-md border border-border p-2 text-left text-[11px] text-muted-foreground">
            {detail}
          </pre>
        )}
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              if (staleCode) { window.location.reload(); return; }
              router.invalidate(); reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-gold-gradient px-4 py-2 text-sm font-medium text-gold-foreground hover:opacity-90"
          >
            Try again
          </button>
          <a href="/" className="inline-flex items-center justify-center rounded-md border border-border px-4 py-2 text-sm">Go home</a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient; locale: Locale }>()({
  loader: ({ location }) => ({ pathname: stripLocalePrefix(location.pathname) }),
  head: ({ loaderData }) => ({

    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "BidNova Auctions | UK's Trusted Trading Card Marketplace" },
      { name: "description", content: "Buy, sell and auction Pokémon, One Piece, Dragon Ball, Yu-Gi-Oh!, sports cards and collectibles securely across the UK." },
      { name: "keywords", content: "BidNova Auctions, Pokemon cards UK, Trading card marketplace, Trading card auctions, Pokemon auction UK, One Piece cards UK, PSA cards, BGS cards, ACE graded cards, Collectibles UK" },
      { name: "author", content: "BidNova Auctions" },
      { name: "theme-color", content: "#0b0b0f" },
      { name: "mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" },
      { name: "apple-mobile-web-app-title", content: "BidNova" },
      { name: "application-name", content: "BidNova" },

      { name: "referrer", content: "strict-origin-when-cross-origin" },
      { name: "format-detection", content: "telephone=no" },
      ...(import.meta.env.VITE_GOOGLE_SITE_VERIFICATION
        ? [{ name: "google-site-verification", content: String(import.meta.env.VITE_GOOGLE_SITE_VERIFICATION) }]
        : []),
      ...(import.meta.env.VITE_BING_SITE_VERIFICATION
        ? [{ name: "msvalidate.01", content: String(import.meta.env.VITE_BING_SITE_VERIFICATION) }]
        : []),
      { property: "og:site_name", content: "BidNova Auctions" },
      { property: "og:title", content: "BidNova Auctions | UK's Trusted Trading Card Marketplace" },
      { property: "og:description", content: "Buy, sell and auction Pokémon, One Piece, Dragon Ball, Yu-Gi-Oh!, sports cards and collectibles securely across the UK." },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: OG_LOCALE[detectLocale()] },
      ...LOCALES.filter((l) => l !== detectLocale()).map((l) => ({
        property: "og:locale:alternate",
        content: OG_LOCALE[l],
      })),
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@bidnovaauctions" },
      { name: "twitter:title", content: "BidNova Auctions | UK's Trusted Trading Card Marketplace" },
      { name: "twitter:description", content: "Buy, sell and auction Pokémon, One Piece, Dragon Ball, Yu-Gi-Oh!, sports cards and collectibles securely across the UK." },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "" },
      ...(import.meta.env.VITE_SUPABASE_URL
        ? [{ rel: "preconnect", href: String(import.meta.env.VITE_SUPABASE_URL), crossOrigin: "" } as const]
        : []),
      ...(import.meta.env.PROD && import.meta.env.VITE_GA4_ID
        ? [
            { rel: "preconnect", href: "https://www.googletagmanager.com" } as const,
            { rel: "preconnect", href: "https://www.google-analytics.com" } as const,
          ]
        : []),
      { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=Sora:wght@500;600;700&family=Manrope:wght@400;500;600;700&display=swap" },
      { rel: "icon", href: "/favicon.png", type: "image/png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      { rel: "manifest", href: "/site.webmanifest" },
      // hreflang alternates for every supported language plus x-default.
      // Locale prefixes must not emit a trailing slash on the home path — the
      // server 307-redirects "/zh-hk/" to "/zh-hk", and hreflang must point at
      // the final 200 URL.
      ...LOCALES.map((l) => {
        const path = (loaderData as { pathname?: string } | undefined)?.pathname ?? "/";
        const prefix = LOCALE_PREFIX[l];
        const suffix = prefix && path === "/" ? "" : path;
        return {
          rel: "alternate",
          hrefLang: SITE_LOCALE_HREFLANG[l],
          href: `https://bidnovaauctions.uk${prefix}${suffix}` || "https://bidnovaauctions.uk/",
        };
      }),
      {
        rel: "alternate",
        hrefLang: "x-default",
        href: `https://bidnovaauctions.uk${(loaderData as { pathname?: string } | undefined)?.pathname ?? "/"}`,
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "BidNova Auctions",
          url: "https://bidnovaauctions.uk",
          logo: "https://bidnovaauctions.uk/favicon.png",
          description: "UK's trusted marketplace to buy, sell and auction Pokémon, One Piece, Dragon Ball, Yu-Gi-Oh!, sports cards and collectibles.",
          sameAs: [],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "BidNova Auctions",
          url: "https://bidnovaauctions.uk",
          potentialAction: {
            "@type": "SearchAction",
            target: "https://bidnovaauctions.uk/auctions?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  const locale = detectLocale();
  return (
    <html lang={locale} suppressHydrationWarning>
      <head suppressHydrationWarning>
        <HeadContent />
      </head>
      <body suppressHydrationWarning className="overflow-x-hidden">
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient, locale } = Route.useRouteContext();
  const router = useRouter();

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event !== "SIGNED_IN" && event !== "SIGNED_OUT" && event !== "USER_UPDATED") return;
      router.invalidate();
      if (event !== "SIGNED_OUT") queryClient.invalidateQueries();
    });
    return () => sub.subscription.unsubscribe();
  }, [router, queryClient]);

  // A code file failed to load (page older than the app's current files).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onPreloadError = (e: Event) => {
      if (reloadOnceForChunkError()) e.preventDefault();
    };
    const onRejection = (e: PromiseRejectionEvent) => {
      if (isChunkLoadError(e.reason)) reloadOnceForChunkError();
    };
    window.addEventListener("vite:preloadError", onPreloadError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("vite:preloadError", onPreloadError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const ric =
      (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number })
        .requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 1));
    const handle = ric(() => { void registerServiceWorker(); }, { timeout: 4000 });
    return () => {
      const cic = (window as unknown as { cancelIdleCallback?: (h: number) => void }).cancelIdleCallback;
      if (cic) cic(handle as number); else window.clearTimeout(handle as number);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const ric =
      (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number })
        .requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 1));
    ric(() => {
      void import("@/lib/sentry-browser").then(({ initSentryClient }) => initSentryClient());
    }, { timeout: 5000 });
  }, []);

  // Analytics loaders — production + cookie consent only. Deferred until idle
  // so they don't compete with hydration / LCP work on mobile.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!import.meta.env.PROD) return;

    const gaId = import.meta.env.VITE_GA4_ID as string | undefined;
    const gtmId = import.meta.env.VITE_GTM_ID as string | undefined;
    const clarityId = import.meta.env.VITE_CLARITY_ID as string | undefined;

    function hasConsent(): boolean {
      try {
        const raw = localStorage.getItem("epa_cookie_consent_v1");
        if (!raw) return false;
        const p = JSON.parse(raw) as { analytics?: boolean; marketing?: boolean };
        return Boolean(p.analytics || p.marketing);
      } catch {
        return false;
      }
    }

    function load() {
      if (!hasConsent()) return;
      const w = window as unknown as { dataLayer?: unknown[]; gtag?: (...a: unknown[]) => void };
      if (gaId && !document.getElementById("ga4-loader")) {
        const s = document.createElement("script");
        s.id = "ga4-loader";
        s.async = true;
        s.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
        document.head.appendChild(s);
        w.dataLayer = w.dataLayer || [];
        w.gtag = function () { w.dataLayer!.push(arguments as unknown as unknown[]); };
        w.gtag("js", new Date());
        w.gtag("config", gaId, { anonymize_ip: true, send_page_view: true });
      }
      if (gtmId && !document.getElementById("gtm-loader")) {
        const s = document.createElement("script");
        s.id = "gtm-loader";
        s.async = true;
        s.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s);j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${gtmId}');`;
        document.head.appendChild(s);
      }
      if (clarityId && !document.getElementById("clarity-loader")) {
        const s = document.createElement("script");
        s.id = "clarity-loader";
        s.innerHTML = `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window,document,"clarity","script","${clarityId}");`;
        document.head.appendChild(s);
      }
    }

    const ric =
      (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number })
        .requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 1));
    const deferredLoad = () => ric(() => load(), { timeout: 6000 });
    deferredLoad();
    const onConsent = () => deferredLoad();
    window.addEventListener("epa:cookie-consent", onConsent as EventListener);
    return () => window.removeEventListener("epa:cookie-consent", onConsent as EventListener);
  }, []);


  // SPA page_view tracking on route change.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const unsub = router.subscribe("onResolved", (e) => {
      const w = window as unknown as { gtag?: (...a: unknown[]) => void };
      if (typeof w.gtag === "function") {
        w.gtag("event", "page_view", { page_path: e.toLocation.pathname, page_location: window.location.href });
      }
    });
    return () => unsub();
  }, [router]);



  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider locale={locale ?? DEFAULT_LOCALE}>
      <CurrencyProvider>
      <AuthProvider>
        <div className="min-h-screen flex flex-col" suppressHydrationWarning>
          <ClientOnly fallback={null}>
            <PushEnableBanner />
          </ClientOnly>
          <SiteHeader />
          <main className="flex-1" suppressHydrationWarning>
            <Outlet />
          </main>
          <SiteFooter />
        </div>
        <ClientOnly fallback={null}>
          <Toaster />
        </ClientOnly>
        <ClientOnly fallback={null}>
          <CookieConsent />
        </ClientOnly>
        <ClientOnly fallback={null}>
          <InstallAppBanner />
        </ClientOnly>
        <ClientOnly fallback={null}>
          <BuyerAssistantWidget />
        </ClientOnly>

      </AuthProvider>
      </CurrencyProvider>
      </I18nProvider>
    </QueryClientProvider>
  );
}
